import { useState } from "react";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import {
  FileBarChart,
  Download,
  RefreshCw,
  Trash2,
  FileText,
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  BarChart3,
  History,
  Sparkles,
} from "lucide-react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import { SideSheet, Badge, ConfirmDialog, Button } from "@/components/ui";
import type { AuditId, AuditStatus, Id } from "@/packages/types";

interface ReportSideSheetProps {
  reportId: Id<"reports"> | null;
  auditId: AuditId;
  auditName: string;
  auditStatus: AuditStatus;
  open: boolean;
  onClose: () => void;
}

function InfoCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl bg-card border border-border overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-2">
      <span className="text-muted">{icon}</span>
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
    </div>
  );
}

function CardBody({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`px-4 pb-4 ${className}`}>{children}</div>;
}

export function ReportSideSheet({
  reportId,
  auditId,
  auditName,
  auditStatus,
  open,
  onClose,
}: ReportSideSheetProps) {
  const { t } = useTranslation(["reports", "common"]);
  const workspaceId = useWorkspaceId();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: report } = useWorkspaceQuery(api.reports.getById, {
    reportId: reportId ?? null,
  });

  const { data: staleness } = useWorkspaceQuery(api.reports.isReportStale, {
    auditId,
  });

  const { data: versionHistory } = useWorkspaceQuery(
    api.reports.getVersionHistory,
    { auditId },
  );

  const generateMutation = useApiMutation(api.reports.generate, {
    name: "regenerate-report",
    successMessage: t("reports:card.generating"),
  });

  const removeMutation = useApiMutation(api.reports.remove, {
    name: "remove-report",
    successMessage: t("reports:sheet.reportDeleted"),
  });

  const handleDownload = () => {
    if (!report?.pdfUrl) return;
    window.open(report.pdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleRegenerate = () => {
    generateMutation.mutateAsync({
      workspaceId,
      auditId,
    });
  };

  const handleDelete = async () => {
    if (!report) return;
    await removeMutation.mutateAsync({
      workspaceId,
      reportId: report.id,
    });
    setShowDeleteConfirm(false);
    onClose();
  };

  const isDraft = (report?.auditStatus ?? auditStatus) !== "approved";
  const metrics = report?.summary.metrics ?? {
    totalChecklistItems: 0,
    completedItems: 0,
    uploadedItems: 0,
    missingItems: 0,
    totalFindings: 0,
    criticalFindings: 0,
    majorFindings: 0,
    minorFindings: 0,
    observationFindings: 0,
    documentCoverage: 0,
  };
  const isApprovalYes = report?.summary.approvalRecommendation === "yes";
  const generateButtonLabel = report
    ? t("reports:sheet.regenerate")
    : t("reports:card.generate");

  return (
    <>
      <SideSheet open={open} onClose={onClose}>
        <SideSheet.Header
          title={t("reports:sheet.title")}
          icon={<FileBarChart size={18} className="text-accent" />}
        />

        <SideSheet.Body className="overflow-y-auto">
          <div className="space-y-3">
            {/* Header info */}
            <InfoCard>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-primary">
                    {report?.auditName ?? auditName}
                  </h3>
                  {isDraft && (
                    <Badge
                      color={{
                        bg: "rgba(161,161,166,0.15)",
                        text: "#a1a1a6",
                      }}
                    >
                      {t("reports:sheet.draft")}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-secondary">
                  {report ? (
                    <>
                      {t("reports:sheet.generatedOn")}{" "}
                      {new Date(report.generatedAt).toLocaleString()}
                      {report.version && (
                        <span className="ml-2 text-muted">
                          &middot;{" "}
                          {t("reports:sheet.version", {
                            version: report.version,
                          })}
                        </span>
                      )}
                    </>
                  ) : (
                    t("reports:sheet.noReport")
                  )}
                </p>
              </div>
            </InfoCard>

            {/* Stale Warning */}
            {report && staleness?.isStale && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                <p className="text-xs text-amber-300">
                  {t("reports:sheet.staleWarning")}
                </p>
              </div>
            )}

            {/* Executive Summary */}
            <InfoCard>
              <CardHeader
                icon={<FileText size={14} />}
                title={t("reports:sheet.executiveSummary")}
              />
              <CardBody>
                {report ? (
                  <p className="text-sm text-secondary leading-relaxed">
                    {report.summary.aiSummary ?? t("reports:sheet.noSummary")}
                  </p>
                ) : (
                  <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/70 bg-card/30 px-6 py-8 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-accent/20 bg-accent/10">
                      <Sparkles size={22} className="text-accent" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">
                        {t("reports:sheet.noReport")}
                      </p>
                      <p className="text-sm text-secondary leading-relaxed">
                        {t("reports:card.noReport")}
                      </p>
                    </div>
                  </div>
                )}
              </CardBody>
            </InfoCard>

            {/* Approval Recommendation */}
            {report?.summary.approvalRecommendation && (
              <div
                className={`rounded-xl border overflow-hidden ${
                  isApprovalYes
                    ? "border-green-500/20 bg-green-500/5"
                    : "border-red-500/20 bg-red-500/5"
                }`}
              >
                <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                  <span
                    className={
                      isApprovalYes ? "text-green-400" : "text-red-400"
                    }
                  >
                    {isApprovalYes ? (
                      <CheckCircle2 size={14} />
                    ) : (
                      <AlertTriangle size={14} />
                    )}
                  </span>
                  <h3 className="text-sm font-semibold text-primary">
                    {t("reports:sheet.recommendation")}
                  </h3>
                </div>
                <div className="px-4 pb-4">
                  <Badge
                    color={
                      isApprovalYes
                        ? { bg: "rgba(74,222,128,0.15)", text: "#4ade80" }
                        : { bg: "rgba(248,113,113,0.15)", text: "#f87171" }
                    }
                  >
                    {isApprovalYes
                      ? t("reports:sheet.recommendYes")
                      : t("reports:sheet.recommendNo")}
                  </Badge>
                </div>
              </div>
            )}

            {/* Metrics Grid */}
            <InfoCard>
              <CardHeader
                icon={<BarChart3 size={14} />}
                title={t("reports:sheet.metrics")}
              />
              <CardBody>
                <div className="grid grid-cols-3 gap-3">
                  {/* Checklist */}
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <ListChecks size={12} className="text-accent" />
                      <span className="text-xs font-medium text-secondary">
                        {t("reports:sheet.checklist")}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-primary">
                      {metrics.completedItems}
                      <span className="text-sm font-normal text-muted">
                        /{metrics.totalChecklistItems}
                      </span>
                    </p>
                    <p className="text-[10px] text-muted">
                      {t("reports:sheet.completed")}
                    </p>
                  </div>

                  {/* Findings */}
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertTriangle size={12} className="text-amber-400" />
                      <span className="text-xs font-medium text-secondary">
                        {t("reports:sheet.findings")}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-primary">
                      {metrics.totalFindings}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {metrics.criticalFindings > 0 && (
                        <span className="text-[10px] text-red-400">
                          {metrics.criticalFindings}{" "}
                          {t("reports:sheet.critical")}
                        </span>
                      )}
                      {metrics.majorFindings > 0 && (
                        <span className="text-[10px] text-amber-400">
                          {metrics.majorFindings} {t("reports:sheet.major")}
                        </span>
                      )}
                      {metrics.minorFindings > 0 && (
                        <span className="text-[10px] text-blue-400">
                          {metrics.minorFindings} {t("reports:sheet.minor")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <FileText size={12} className="text-green-400" />
                      <span className="text-xs font-medium text-secondary">
                        {t("reports:sheet.coverage")}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-primary">
                      {metrics.documentCoverage}%
                    </p>
                  </div>
                </div>
              </CardBody>
            </InfoCard>

            {/* Version History */}
            {report && versionHistory && versionHistory.length > 1 && (
              <InfoCard>
                <CardHeader
                  icon={<History size={14} />}
                  title={t("reports:sheet.versionHistory")}
                />
                <CardBody>
                  <div className="space-y-1">
                    {versionHistory.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          // Handled by parent — navigate to this version
                        }}
                        className="w-full flex items-center justify-between rounded-lg border border-border px-3 py-2 text-xs hover:bg-card/80 transition-colors"
                      >
                        <span className="font-medium text-primary">
                          {t("reports:sheet.version", { version: v.version })}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-muted">
                            {new Date(v.generatedAt).toLocaleDateString()}
                          </span>
                          {v.isLatest && (
                            <Badge
                              color={{
                                bg: "rgba(74,222,128,0.15)",
                                text: "#4ade80",
                              }}
                            >
                              {t("reports:sheet.latestBadge")}
                            </Badge>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </InfoCard>
            )}
          </div>
        </SideSheet.Body>

        <SideSheet.Footer>
          <div className="flex items-center justify-between w-full gap-2">
            {report ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                <Trash2 size={13} />
                {t("reports:sheet.deleteReport")}
              </button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                icon={RefreshCw}
                loading={generateMutation.isPending}
                onClick={handleRegenerate}
              >
                {generateButtonLabel}
              </Button>

              {report?.pdfUrl && (
                <Button
                  variant="secondary"
                  icon={Download}
                  onClick={handleDownload}
                >
                  {t("reports:sheet.downloadPdf")}
                </Button>
              )}
            </div>
          </div>
        </SideSheet.Footer>
      </SideSheet>

      <ConfirmDialog
        open={showDeleteConfirm && report !== null}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("reports:sheet.deleteConfirmTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            {t("reports:sheet.deleteConfirmDescription")}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmLabel={t("common:buttons.delete")}
          cancelLabel={t("common:buttons.cancel")}
          variant="danger"
          loading={removeMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
